import React from "react";
import { LucideIcon } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export interface PageHeaderProps {
  title: string;
  description?: string;
  icon: LucideIcon;
  iconBgColor?: string;
  gradient?: string;
  actions?: React.ReactNode;
  avatar?: {
    src?: string;
    fallback: string;
  };
  userInfo?: {
    username?: string;
    role?: string;
  };
}

export function PageHeader({
  title,
  description,
  icon: Icon,
  iconBgColor = "bg-icon-background",
  gradient = "from-header-primary to-header-primary",
  actions,
  avatar,
  userInfo,
}: PageHeaderProps) {
  // Determine text color class based on gradient
  const getTextColorClass = () => {
    if (gradient.includes('header-primary')) return 'text-header-primary-text';
    if (gradient.includes('header-yellow')) return 'text-header-yellow-text';
    if (gradient.includes('header-purple')) return 'text-header-purple-text';
    if (gradient.includes('header-pink')) return 'text-header-pink-text';
    return 'text-white'; // fallback
  };

  const textColorClass = getTextColorClass();

  return (
    <div className="bg-white border-b border-sidebar-border shadow-sm h-20 md:h-24">
      <div className="h-full px-4 md:px-6 flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-0">
        <div className="flex items-center gap-4">
          {avatar && (
            <Avatar className="h-12 w-12">
              <AvatarImage src={avatar.src} />
              <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground font-semibold">
                {avatar.fallback}
              </AvatarFallback>
            </Avatar>
          )}
          {!avatar && (
            <div className="bg-white p-3 rounded-lg">
              <Icon className="h-8 w-8 text-sidebar-foreground" />
            </div>
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-sidebar-foreground text-left">
              {title}
            </h1>
            {description && (
              <p className="text-sidebar-muted-foreground text-sm md:text-base mt-1 text-left">
                {description}
              </p>
            )}
            {userInfo && (
              <div className="mt-2">
                {userInfo.username && (
                  <p className="text-sidebar-muted-foreground text-sm">@{userInfo.username}</p>
                )}
                {userInfo.role && (
                  <p className="text-sidebar-muted-foreground text-xs uppercase tracking-wide">
                    {userInfo.role}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}